import { useCallback, useRef } from 'react';

import {
  useGetApiV5FilesIdNamingSuggestions,
  usePostApiV5FilesIdAcceptNamingSuggestion,
} from '@liscio/api';

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 60; // 2 minutes

interface UseDeterministicNamingOptions {
  fileUuid: string;
  enabled?: boolean;
}

export const useDeterministicNaming = ({
  fileUuid,
  enabled = true,
}: UseDeterministicNamingOptions) => {
  const pollCount = useRef(0);

  const {
    data: suggestionData,
    isLoading: isSuggestionLoading,
    isError: isSuggestionError,
  } = useGetApiV5FilesIdNamingSuggestions(fileUuid, {
    query: {
      // Always fetch once (to get preview_values from metadata for the modal).
      // Only poll repeatedly when enabled (document still processing).
      enabled: !!fileUuid,
      refetchInterval: enabled
        ? (data, query) => {
            if (query.state.status === 'error') return false;
            if (data?.suggestion) return false;
            if (pollCount.current >= MAX_POLL_ATTEMPTS) return false;
            pollCount.current += 1;
            return POLL_INTERVAL_MS;
          }
        : false,
      staleTime: 0,
    },
  });

  const isTimedOut = pollCount.current >= MAX_POLL_ATTEMPTS;
  const isProcessing = enabled && !isSuggestionError && !isTimedOut &&
    (isSuggestionLoading || !suggestionData?.suggestion);

  const {
    mutateAsync: acceptMutation,
    isLoading: isAccepting,
  } = usePostApiV5FilesIdAcceptNamingSuggestion();

  const accept = useCallback(
    (editedName?: string) => {
      const body = editedName ? { name: editedName } : {};
      return acceptMutation({ id: fileUuid, data: body });
    },
    [fileUuid, acceptMutation],
  );

  return {
    suggestion: suggestionData?.suggestion ?? null,
    source: suggestionData?.source ?? null,
    previewValues: suggestionData?.preview_values ?? null,
    isProcessing,
    isSuggestionError,
    accept,
    isAccepting,
  };
};
